import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import { X as XIcon, ArrowRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { ThemedText } from './ThemedText';
import { faqService } from '../api';
import { Collapsible } from './Collapsible';
import { useNavigate } from 'react-router-dom';
import { getCategoryIcon } from '../constants/faqCategories';

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p','br','strong','b','em','i','u','s','a','img','ul','ol','li','blockquote','code','pre','h1','h2','h3','h4','h5','h6','span','div'],
  ALLOWED_ATTR: ['href','src','alt','style','target','rel','class','width','height'],
};

const sanitizeHtml = (html) => DOMPurify.sanitize(html || '', SANITIZE_CONFIG);

const stripHtmlTags = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html || '';
  return (tmp.textContent || tmp.innerText || '').trim();
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: ${props => props.theme.spacing.lg};
`;

const Modal = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.lg};
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${props => props.theme?.shadows?.lg};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${props => props.theme.colors.text};
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => props.theme.borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.colors.backgroundSecondary};
  }
`;

const ModalBody = styled.div`
  padding: ${props => props.theme.spacing.lg};
  overflow-y: auto;
  flex: 1;
`;

const SearchContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.sizes.default};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  color: ${props => props.theme.colors.text};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }

  &::placeholder {
    color: ${props => props.theme.colors.muted};
  }
`;

const CategorySection = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const CategoryTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.typography.sizes.large};
  font-weight: 600;
  color: ${props => props.theme.colors.tint};
  margin-bottom: ${props => props.theme.spacing.md};
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 2px solid ${props => props.theme.colors.border};
`;

const FaqItem = styled.div`
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const RichTextContent = styled.div`
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.text};
  line-height: 1.6;
  font-size: ${props => props.theme.typography.sizes.default};

  h1, h2, h3 { color: ${props => props.theme.colors.text}; margin: ${props => props.theme.spacing.md} 0 ${props => props.theme.spacing.sm}; }
  p { margin: ${props => props.theme.spacing.sm} 0; }
  ul, ol { padding-left: ${props => props.theme.spacing.xl}; }
  code { background: ${props => props.theme.colors.background}; padding: 2px 6px; border-radius: 4px; font-size: 0.9em; }
  pre { background: ${props => props.theme.colors.background}; padding: ${props => props.theme.spacing.md}; border-radius: ${props => props.theme.borderRadius.md}; overflow-x: auto; }
  a { color: ${props => props.theme.colors.tint}; }
  img { max-width: 100%; border-radius: ${props => props.theme.borderRadius.md}; }
  blockquote { border-left: 3px solid ${props => props.theme.colors.border}; padding-left: ${props => props.theme.spacing.md}; color: ${props => props.theme.colors.muted}; margin: ${props => props.theme.spacing.sm} 0; }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.muted};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.muted};
`;

const ModalFooter = styled.div`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: center;
`;

const ViewAllLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: ${props => props.theme.colors.tint};
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: 600;
  cursor: pointer;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.tint + '10'};
  }
`;

export const FAQModal = ({ isOpen, onClose, organizationId = null }) => {
  const theme = useTheme();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      fetchFaqs();
    }
  }, [isOpen, organizationId]);

  const fetchFaqs = async () => {
    try {
      setLoading(true);
      const data = await faqService.getPublicFaqs(organizationId);
      setFaqs(data);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  // Filter FAQs based on search term (strip HTML before searching answer)
  const filteredFaqs = faqs.filter(faq => {
    const term = searchTerm.toLowerCase();
    const plainAnswer = stripHtmlTags(faq.answer);
    return faq.question.toLowerCase().includes(term) ||
      plainAnswer.toLowerCase().includes(term) ||
      (faq.category && faq.category.toLowerCase().includes(term));
  });

  // Group FAQs by category
  const groupedFaqs = faqService.groupByCategory(filteredFaqs);

  return (
    <ModalOverlay theme={theme} isOpen={isOpen} onClick={handleClose}>
      <Modal theme={theme} onClick={e => e.stopPropagation()}>
        <ModalHeader theme={theme}>
          <ThemedText type="subtitle">Frequently Asked Questions</ThemedText>
          <CloseButton theme={theme} onClick={handleClose}>
            <XIcon size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody theme={theme}>
          <SearchContainer theme={theme}>
            <SearchInput
              theme={theme}
              type="text"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </SearchContainer>

          {loading ? (
            <LoadingContainer theme={theme}>
              <ThemedText>Loading FAQs...</ThemedText>
            </LoadingContainer>
          ) : filteredFaqs.length === 0 ? (
            <EmptyState theme={theme}>
              <ThemedText>
                {searchTerm
                  ? 'No FAQs found matching your search.'
                  : 'No FAQs available yet.'}
              </ThemedText>
            </EmptyState>
          ) : (
            Object.entries(groupedFaqs).filter(([key]) => key !== '_categoryIcons').map(([category, categoryFaqs]) => {
              const iconOverride = categoryFaqs.find(f => f.categoryIcon)?.categoryIcon;
              const CatIcon = getCategoryIcon(category, iconOverride);
              return (
                <CategorySection key={category} theme={theme}>
                  <CategoryTitleRow theme={theme}>
                    <CatIcon size={18} />
                    {category}
                  </CategoryTitleRow>
                  {categoryFaqs.map(faq => (
                    <FaqItem key={faq.id} theme={theme}>
                      <Collapsible title={faq.question}>
                        <RichTextContent
                          theme={theme}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(faq.answer) }}
                        />
                      </Collapsible>
                    </FaqItem>
                  ))}
                </CategorySection>
              );
            })
          )}
        </ModalBody>
        <ModalFooter theme={theme}>
          <ViewAllLink
            theme={theme}
            onClick={() => {
              handleClose();
              navigate('/help');
            }}
          >
            View all FAQs <ArrowRight size={16} />
          </ViewAllLink>
        </ModalFooter>
      </Modal>
    </ModalOverlay>
  );
};
